// TDS2 SHIM — composes SystemErrorState; no direct TDS2 counterpart.
// Specialized error state for LLM Gateway 4xx/5xx responses. The backend
// raises errors in the format `LLM Gateway error <code>: <body>` where the
// body is JSON shaped like:
//   { errorCode, messageCode, message, parameters: [{name, value}, ...] }
// The longest human-readable string usually lives in `parameters[1].value`
// (the underlying upstream message). We surface that as the body and tuck
// the full payload into a <details> disclosure for support handoff.
import React from 'react';
import PropTypes from 'prop-types';
import SystemErrorState from './SystemErrorState';

const GATEWAY_PREFIX_REGEX = /^LLM Gateway error\s+(\d{3})\s*:\s*/i;

function parseGatewayError(rawMessage) {
  if (!rawMessage) return { statusCode: null, payload: null };
  const match = rawMessage.match(GATEWAY_PREFIX_REGEX);
  const statusCode = match ? parseInt(match[1], 10) : null;
  const jsonPart = match ? rawMessage.slice(match[0].length) : rawMessage;
  let payload = null;

  try {
    payload = JSON.parse(jsonPart);
  } catch (e) {
    payload = null;
  }
  return { statusCode: statusCode, payload: payload };
}

function pickHumanMessage(payload) {
  if (!payload) return null;
  // The gateway puts the upstream LLM/Bedrock message in parameters[*].value.
  // Pick the longest value — that's almost always the descriptive one.
  if (Array.isArray(payload.parameters) && payload.parameters.length > 0) {
    const longest = payload.parameters
      .map((p) => (p && typeof p.value === 'string' ? p.value : ''))
      .reduce((a, b) => (b.length > a.length ? b : a), '');
    if (longest && longest.length > (payload.message || '').length) {
      return longest;
    }
  }
  return payload.message || null;
}

function CircleAlertIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export default function LlmGatewayErrorState({
  rawMessage,
  mascotUrl,
  title = 'The model couldn’t respond',
  toastHeading = 'The model couldn’t respond',
  toastMessage = 'The AI gateway returned an error. Try again in a moment.',
  retryLabel = 'Try again',
  onRetry,
}) {
  const parsed = parseGatewayError(rawMessage);
  const human = pickHumanMessage(parsed.payload);
  const errorCode = parsed.payload && parsed.payload.errorCode;
  const messageCode = parsed.payload && parsed.payload.messageCode;

  const bodyText =
    human ||
    (parsed.payload && parsed.payload.message) ||
    rawMessage ||
    'The LLM Gateway returned an error.';

  const codeLine = errorCode || messageCode
    ? [errorCode, messageCode].filter(Boolean).join(' · ')
    : null;

  const body = (
    <React.Fragment>
      <p>{bodyText}</p>
      {codeLine ? (
        <p className="tds2-agentforce-state-card__code-line">
          <code>{codeLine}</code>
        </p>
      ) : null}
    </React.Fragment>
  );

  const rawJson = parsed.payload
    ? JSON.stringify(parsed.payload, null, 2)
    : rawMessage || '';

  const details = rawJson ? (
    <details className="tds2-agentforce-state-card__disclosure">
      <summary>Show raw response</summary>
      <pre className="tds2-agentforce-state-card__pre">{rawJson}</pre>
    </details>
  ) : null;

  const actions = onRetry ? (
    <button
      type="button"
      className="tds2-button tds2-button--primary tds2-button--small"
      data-tds2-shim="Button"
      onClick={onRetry}
    >
      <span className="tds2-button__label">{retryLabel}</span>
    </button>
  ) : null;

  return (
    <SystemErrorState
      variant="danger"
      toastHeading={toastHeading}
      toastMessage={toastMessage}
      mascotUrl={mascotUrl}
      icon={<CircleAlertIcon />}
      title={title}
      body={body}
      details={details}
      actions={actions}
    />
  );
}

LlmGatewayErrorState.propTypes = {
  rawMessage: PropTypes.string,
  mascotUrl: PropTypes.string,
  title: PropTypes.string,
  toastHeading: PropTypes.string,
  toastMessage: PropTypes.string,
  retryLabel: PropTypes.string,
  onRetry: PropTypes.func,
};

export { parseGatewayError, pickHumanMessage };
